/**
 * DashboardSection — section primitive for Billing + Admin surfaces.
 *
 * Jun 13, 2026 realignment: typography snapped to the same Brand Kit
 * pencil-clean language as ``SettingsPanel`` ``variant="flow"`` so the
 * whole Settings + Admin area reads with one design voice. Previously
 * the header was uppercase 11px ``text-ink-3`` (legacy admin-v2
 * widget) — that looked like a Notion 2019 "DATABASE" eyebrow next to
 * the new font-display 15px ink-1 panels, hence the user's
 * "Fatturazione non mi sembra allineata" feedback.
 *
 * New header anatomy (matches SettingsPanel flow):
 *   - ``<h3>`` ``font-display`` Space Grotesk, semibold, 15px, ink-1,
 *     ``tracking-[-0.012em]`` — same as section titles in the rest of
 *     Settings.
 *   - subtitle ``text-[12.5px] text-ink-3 leading-relaxed`` — same.
 *   - ``py-7 first:pt-2 last:pb-2`` — same vertical rhythm so flowed
 *     siblings read as a continuous stack without dividers.
 *
 * The wrapper still does NOT paint a card border — Billing's sub-cards
 * (``CurrentPlanCard`` etc.) bring their own ``rounded-lg border``;
 * double-bordering would muddy the data widgets.
 */

import * as React from "react";

import { cn } from "@ando/ui";

export interface DashboardSectionProps {
  /** Italian title shown in font-display 15px ink-1. */
  readonly title: string;
  /** Optional one-line hint below the title (12.5px, ``text-ink-3``). */
  readonly subtitle?: string;
  /** Optional trailing slot on the header row (e.g. action button). */
  readonly headerTrailing?: React.ReactNode;
  /** Body content. */
  readonly children: React.ReactNode;
  readonly className?: string;
}

export const DashboardSection: React.FC<DashboardSectionProps> = ({
  title,
  subtitle,
  headerTrailing,
  children,
  className,
}) => {
  const headingId = React.useId();
  return (
    <section
      aria-labelledby={headingId}
      className={cn("flex flex-col py-7 first:pt-2 last:pb-2", className)}
    >
      <header className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3
            id={headingId}
            className="font-display font-semibold text-[15px] text-ink-1 tracking-[-0.012em]"
          >
            {title}
          </h3>
          {subtitle ? (
            <p className="mt-1 text-[12.5px] text-ink-3 leading-relaxed">
              {subtitle}
            </p>
          ) : null}
        </div>
        {headerTrailing ? <div className="shrink-0">{headerTrailing}</div> : null}
      </header>
      <div>{children}</div>
    </section>
  );
};
DashboardSection.displayName = "DashboardSection";
